// src/components/dependants/sharedNoteEditor.component.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "quill/dist/quill.snow.css";
import { getUserFromToken } from "../../../utils/functions.ts";
import { useNavBar } from "../../../Contexts/navbar.items.context.jsx";
import { useMask } from "../../../Contexts/mask.context.jsx";

export default function SharedNoteEditor({
  note,
  readOnly = false,
  placeholder = "Enter Note",
}) {
  const notesEndpoint = "https://www.fbxchange.com/ReactPHP/notes.php";
  const { mask, unmask } = useMask();
  const { updateActiveSideButton } = useNavBar();

  const initialValue = useMemo(() => {
    if (typeof note === "string") return note || "";
    if (note && typeof note === "object") {
      return note.theNote ?? note.Communication ?? note.html ?? "";
    }
    return "";
  }, [note]);

  const [displayNote, setDisplayNote] = useState(initialValue);
  const [dirty, setDirty] = useState(false);
  const unmaskRef = useRef(unmask);

  const originalRef = useRef(
    typeof note === "object" && note !== null
      ? JSON.parse(JSON.stringify(note))
      : initialValue
  );

  const quillRef = useRef(null);

  useEffect(() => {
    unmaskRef.current = unmask;
  }, [unmask]);

  useEffect(() => {
    return () => {
      try {
        unmaskRef.current();
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    setDisplayNote(initialValue);
    setDirty(false);

    originalRef.current =
      typeof note === "object" && note !== null
        ? JSON.parse(JSON.stringify(note))
        : initialValue;
  }, [initialValue, note]);

  useEffect(() => {
    try {
      if (dirty) mask();
      else unmask();
    } catch (e) {}
  }, [dirty, mask, unmask]);

  const formats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      "code-block",
      "color",
      "background",
      "list",
      "indent",
      "align",
      "link",
      "image",
    ],
    []
  );

  const getOriginalHtml = () => {
    const original = originalRef.current;
    if (typeof original === "string") return original || "";
    if (original && typeof original === "object") {
      return original.theNote ?? original.Communication ?? original.html ?? "";
    }
    return "";
  };

  const handleChange = (content) => {
    setDisplayNote(content);

    if (note && typeof note === "object") {
      note.theNote = content;
    }

    const originalHtml = getOriginalHtml();
    setDirty(content !== originalHtml);
  };

  const letsSaveTheNote = async () => {
    const user = await getUserFromToken();

    try {
      const payload = {
        note,
        original: originalRef.current,
        scriptName: "saveSharedTeamNote",
        user,
      };

      const res = await fetch(notesEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Save failed:", res.status, text);
        return;
      }

      originalRef.current =
        typeof note === "object" && note !== null
          ? JSON.parse(JSON.stringify(note))
          : displayNote;

      setDirty(false);
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  const letsCloseTheNote = () => {
    setDirty(false);
    unmask();
    updateActiveSideButton(null);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
      },
      clipboard: { matchVisual: false },
    }),
    []
  );

  return (
    <div className="note-editor-scroll w-100">
      <div className="d-flex align-items-center justify-content-end gap-2 mt-2 mb-2">
        <button
          type="button"
          className={`btn ${dirty ? "btn-success" : "btn-outline-success"}`}
          disabled={!dirty}
          onClick={letsSaveTheNote}
        >
          Update
        </button>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={letsCloseTheNote}
        >
          Cancel
        </button>
      </div>

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={displayNote}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={!!readOnly}
      />

      <style>{`
        .note-editor-scroll {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding-top: 6px;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .note-editor-scroll::-webkit-scrollbar { width: 0; height: 0; display: none; }
        .note-editor-scroll .ql-toolbar {
          position: sticky;
          top: 0;
          z-index: 20;
          background: var(--bs-body-bg, #fff);
          border-top-left-radius: .25rem;
          border-top-right-radius: .25rem;
        }
        .note-editor-scroll .ql-container {
          min-height: 300px;
          overflow-x: hidden;
          max-height: 600px;
        }
        .note-editor-scroll .ql-editor {
          min-height: 300px;
          overflow-x: hidden;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
      `}</style>
    </div>
  );
}
